import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { trackSignup, clearUtm, useUtmCapture } from "@/lib/utm";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import {
  Loader2, UserPlus, Eye, EyeOff, AlertCircle, CheckCircle2, Circle,
  ArrowRight, ShieldCheck, Sparkles,
} from "lucide-react";

const BRAND_IMAGE = "https://customer-assets-39nsmqrw.emergentagent.net/job_talent-hub-822/artifacts/nu69cgy8_WhatsApp%20Image%202026-08-06%20at%2002.38.47.jpeg";
const REG_ROLES = ["student", "association", "mentor", "content_creator", "donor", "recruiter", "faculty"];

function referralClickTime(code) {
  if (!code) return "";
  const key = `emad-referral-${code.toUpperCase()}`;
  const existing = localStorage.getItem(key);
  if (existing && Date.now() - new Date(existing).getTime() <= 86400000) return existing;
  const created = new Date().toISOString();
  localStorage.setItem(key, created);
  return created;
}

function passwordChecks(pwd) {
  return {
    length: pwd.length >= 6,
    letter: /[A-Za-z\u0600-\u06FF]/.test(pwd),
    digit: /\d/.test(pwd),
  };
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useUtmCapture();
  const initialRole = REG_ROLES.includes(searchParams.get("role")) ? searchParams.get("role") : "student";
  const initialAmbassadorCode = (searchParams.get("ambassador") || "").toUpperCase();
  const initialReferralToken = searchParams.get("referral_token") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: initialRole,
    gender: "",
    phone: "",
    city: "",
    organization_name: "",
    specialization: "",
    referral_source: initialAmbassadorCode ? "ambassador" : "",
    referral_other: "",
    referral_code: initialAmbassadorCode,
    referral_clicked_at: referralClickTime(initialAmbassadorCode),
    referral_token: initialReferralToken,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState({});
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [ambassadorInfo, setAmbassadorInfo] = useState(null);
  const [referralAccepted, setReferralAccepted] = useState(Boolean(initialAmbassadorCode));
  const [validatingReferral, setValidatingReferral] = useState(false);

  const validateReferralCode = async (rawCode = form.referral_code, suppliedToken = "") => {
    const code = (rawCode || "").trim().toUpperCase();
    if (!code) { setAmbassadorInfo(null); setReferralAccepted(false); return; }
    setValidatingReferral(true);
    try {
      const { data } = await api.get(`/ambassadors/code/${encodeURIComponent(code)}`, { params: suppliedToken ? { token: suppliedToken } : {} });
      setAmbassadorInfo(data);
      setReferralAccepted(true);
      setForm((current) => ({ ...current, referral_source: "ambassador", referral_code: data.code, referral_clicked_at: referralClickTime(data.code), referral_token: data.referral_token }));
      setError("");
    } catch (requestError) {
      setAmbassadorInfo(null);
      setReferralAccepted(false);
      setError(formatApiError(requestError));
    } finally { setValidatingReferral(false); }
  };

  useEffect(() => {
    if (initialAmbassadorCode) validateReferralCode(initialAmbassadorCode, initialReferralToken);
    // The query-string code is intentionally validated only once on entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAmbassadorCode]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const pwdChecks = useMemo(() => passwordChecks(form.password), [form.password]);
  const pwdOk = pwdChecks.length && pwdChecks.letter && pwdChecks.digit;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const nameOk = form.name.trim().length >= 2;
  const genderOk = form.gender === "ذكر" || form.gender === "أنثى";
  const orgRequired = ["association", "donor", "recruiter", "faculty"].includes(form.role);
  const orgOk = orgRequired ? form.organization_name.trim().length >= 2 : true;
  const referralOk = form.referral_source !== "ambassador" || (Boolean(ambassadorInfo) && referralAccepted);
  const canSubmit = nameOk && emailOk && pwdOk && genderOk && orgOk && agreedTerms && referralOk;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, password: true, gender: true, organization_name: true, terms: true });
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      const payload = { ...form };
      if (!orgRequired) delete payload.organization_name;
      if (!["mentor", "content_creator"].includes(payload.role)) delete payload.specialization;
      if (payload.referral_source !== "other") delete payload.referral_other;
      if (!payload.referral_source || !referralAccepted) { delete payload.referral_source; delete payload.referral_other; delete payload.referral_code; delete payload.referral_clicked_at; delete payload.referral_token; }
      await register(payload);
      trackSignup(payload.role);
      clearUtm();
      toast.success("تم إنشاء حسابك بنجاح 🎉");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (invalid) =>
    `w-full rounded-xl border-2 bg-slate-50/50 px-4 py-3 text-slate-900 focus:ring-4 outline-none transition-all ${
      invalid
        ? "border-red-300 focus:border-red-500 focus:ring-red-500/10"
        : "border-slate-200 focus:border-teal-600 focus:ring-teal-600/10 focus:bg-white"
    }`;

  return (
    <div
      className="min-h-screen-safe relative overflow-hidden"
      data-testid="register-page"
    >
      {/* Full-screen background image */}
      <img
        src={BRAND_IMAGE}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      {/* Dark overlays — branded teal/rose tint over the full screen */}
      <div className="absolute inset-0 bg-gradient-to-bl from-teal-950/90 via-teal-900/75 to-rose-900/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
      <div className="pointer-events-none absolute top-0 start-0 w-[600px] h-[600px] rounded-full bg-amber-400/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 end-0 w-[500px] h-[500px] rounded-full bg-teal-400/8 blur-3xl" />

      <div className="relative min-h-screen-safe grid lg:grid-cols-[7fr_5fr] gap-0">
        {/* Form on the LEFT (matches RTL reading flow — swapping the visual to the right) */}
        <main className="flex items-center justify-center p-4 sm:p-10 lg:p-14 order-2 lg:order-1">
          <div className="w-full max-w-lg">
            <div className="lg:hidden flex items-center justify-between mb-8">
              <Link to="/" className="block">
                <Logo variant="transparent" size={80} textColor="text-white" subColor="text-teal-200/80" />
              </Link>
              <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-teal-100 hover:text-white font-medium" data-testid="reg-back-home">
                <ArrowRight className="h-4 w-4" /> الرئيسية
              </Link>
            </div>

            <div className="relative rounded-[28px] bg-white/95 backdrop-blur-xl shadow-2xl shadow-teal-950/40 border border-white/20 overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-400 via-amber-300 to-rose-300" />

              <div className="p-5 sm:p-10">
                <div className="hidden lg:flex items-center justify-end mb-6">
                  <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-teal-700 font-medium transition-colors" data-testid="reg-back-home">
                    <ArrowRight className="h-3.5 w-3.5" /> العودة للرئيسية
                  </Link>
                </div>

                <div className="text-[11px] font-bold uppercase tracking-widest text-teal-700 mb-2">
                  انضمام جديد
                </div>
                <h1 className="text-2xl font-heading font-black text-slate-900 leading-tight">
                  خطوتك الأولى في <span className="text-teal-700">عماد</span>
                </h1>
                <p className="mt-2 text-slate-600 text-sm">
                  اختر دورك وأدخل بياناتك للبدء برحلة تدريب تعاوني موثّقة.
                </p>

                <form onSubmit={submit} noValidate className="mt-7 space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2.5">اختر دورك</label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {REG_ROLES.map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => update("role", r)}
                          className={`px-2 py-2.5 rounded-lg text-xs sm:text-[13px] font-semibold border-2 transition-all ${
                            form.role === r
                              ? "bg-teal-700 text-white border-teal-700 shadow-md shadow-teal-900/20"
                              : "bg-white text-slate-700 border-slate-200 hover:border-teal-400 hover:bg-teal-50/40"
                          }`}
                          data-testid={`role-${r}-btn`}
                        >
                          {ROLE_LABELS[r]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reg-name-input" className="block text-sm font-semibold text-slate-700 mb-2">الاسم الكامل</label>
                    <input
                      id="reg-name-input"
                      required minLength={2}
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      onBlur={() => markTouched("name")}
                      placeholder="محمد أحمد"
                      className={inputCls(touched.name && !nameOk)}
                      data-testid="reg-name-input"
                      aria-label="الاسم الكامل"
                    />
                    {touched.name && !nameOk && (
                      <div className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="reg-name-error">
                        <AlertCircle className="h-3.5 w-3.5" /> الاسم مطلوب (حرفان على الأقل)
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">الجنس</label>
                    <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="الجنس" data-testid="reg-gender-group">
                      {["ذكر", "أنثى"].map((g) => (
                        <button
                          key={g}
                          type="button"
                          role="radio"
                          aria-checked={form.gender === g}
                          onClick={() => { update("gender", g); markTouched("gender"); }}
                          className={`px-3 py-3 rounded-xl border-2 text-sm font-bold transition-all min-h-[44px] ${
                            form.gender === g
                              ? "bg-teal-700 text-white border-teal-700 shadow-md shadow-teal-900/20"
                              : "bg-white text-slate-700 border-slate-200 hover:border-teal-400 hover:bg-teal-50/40"
                          }`}
                          data-testid={`reg-gender-${g === "ذكر" ? "male" : "female"}-btn`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                    {touched.gender && !genderOk && (
                      <div className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="reg-gender-error">
                        <AlertCircle className="h-3.5 w-3.5" /> اختر الجنس للمتابعة
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="reg-email-input" className="block text-sm font-semibold text-slate-700 mb-2">البريد الإلكتروني</label>
                      <input
                        id="reg-email-input"
                        type="email" required
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        onBlur={() => markTouched("email")}
                        className={inputCls(touched.email && !emailOk)}
                        data-testid="reg-email-input"
                        dir="ltr"
                        aria-label="البريد الإلكتروني"
                      />
                      {touched.email && !emailOk && (
                        <div className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="reg-email-error">
                          <AlertCircle className="h-3.5 w-3.5" /> بريد غير صحيح
                        </div>
                      )}
                    </div>
                    <div>
                      <label htmlFor="reg-password-input" className="block text-sm font-semibold text-slate-700 mb-2">كلمة المرور</label>
                      <div className="relative">
                        <input
                          id="reg-password-input"
                          type={showPassword ? "text" : "password"} required minLength={6}
                          value={form.password}
                          onChange={(e) => update("password", e.target.value)}
                          onBlur={() => markTouched("password")}
                          className={`${inputCls(touched.password && !pwdOk)} pe-12`}
                          data-testid="reg-password-input"
                          dir="ltr"
                          aria-label="كلمة المرور"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          className="absolute inset-y-0 end-3 my-auto h-8 w-8 grid place-items-center rounded-lg text-teal-800 hover:text-teal-900 hover:bg-teal-50 transition-colors"
                          aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                          data-testid="reg-password-toggle"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl bg-teal-50/60 border border-teal-100 p-3 text-xs" data-testid="reg-password-checklist">
                    <div className="font-semibold text-slate-700 mb-2">متطلبات كلمة المرور:</div>
                    <ul className="grid grid-cols-1 gap-1.5">
                      <ReqRow ok={pwdChecks.length} text="6 أحرف على الأقل" testid="req-length" />
                      <ReqRow ok={pwdChecks.letter} text="حرف واحد على الأقل (عربي أو إنجليزي)" testid="req-letter" />
                      <ReqRow ok={pwdChecks.digit} text="رقم واحد على الأقل" testid="req-digit" />
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="reg-phone-input" className="block text-sm font-semibold text-slate-700 mb-2">رقم الجوال</label>
                      <input
                        id="reg-phone-input"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="05xxxxxxxx"
                        className={inputCls(false)}
                        data-testid="reg-phone-input"
                        dir="ltr"
                        aria-label="رقم الجوال"
                      />
                    </div>
                    <div>
                      <label htmlFor="reg-city-input" className="block text-sm font-semibold text-slate-700 mb-2">المدينة</label>
                      <input
                        id="reg-city-input"
                        value={form.city}
                        onChange={(e) => update("city", e.target.value)}
                        placeholder="الرياض"
                        className={inputCls(false)}
                        data-testid="reg-city-input"
                        aria-label="المدينة"
                      />
                    </div>
                  </div>

                  {orgRequired && (
                    <div>
                      <label htmlFor="reg-org-input" className="block text-sm font-semibold text-slate-700 mb-2">اسم الجهة</label>
                      <input
                        id="reg-org-input"
                        value={form.organization_name}
                        onChange={(e) => update("organization_name", e.target.value)}
                        onBlur={() => markTouched("organization_name")}
                        placeholder="اسم الجمعية أو المؤسسة"
                        className={inputCls(touched.organization_name && !orgOk)}
                        data-testid="reg-org-input"
                        aria-label="اسم الجهة"
                      />
                      {touched.organization_name && !orgOk && (
                        <div className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="reg-org-error">
                          <AlertCircle className="h-3.5 w-3.5" /> اسم الجهة مطلوب
                        </div>
                      )}
                    </div>
                  )}

                  {["mentor", "content_creator", "faculty"].includes(form.role) && (
                    <div>
                      <label htmlFor="reg-spec-input" className="block text-sm font-semibold text-slate-700 mb-2">التخصص</label>
                      <input
                        id="reg-spec-input"
                        value={form.specialization}
                        onChange={(e) => update("specialization", e.target.value)}
                        placeholder="التسويق الرقمي، تقنية المعلومات..."
                        className={inputCls(false)}
                        data-testid="reg-spec-input"
                        aria-label="التخصص"
                      />
                    </div>
                  )}

                  {form.referral_source === "ambassador" && ambassadorInfo && (
                    <div className="rounded-xl bg-teal-50 p-4 text-teal-950" data-testid="ambassador-referral-consent">
                      <div className="flex items-start gap-3">
                        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
                        <div className="flex-1">
                          <div className="font-black">دعوة من سفير عماد: {ambassadorInfo.ambassador_name}</div>
                          <p className="mt-1 text-sm text-teal-900">إذا وافقت، تُحتسب إحالتك للسفير بعد إكمال ملفك. لن تظهر له بياناتك الشخصية.</p>
                          <label className="mt-3 flex items-center gap-2 text-sm font-bold">
                            <input type="checkbox" checked={referralAccepted} onChange={(e) => setReferralAccepted(e.target.checked)} className="h-5 w-5" />
                            أوافق على ربط انضمامي بهذا السفير
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="reg-referral-input" className="block text-sm font-semibold text-slate-700 mb-2">
                      كيف عرفت عن عماد؟ <span className="text-slate-400 font-normal">(اختياري)</span>
                    </label>
                    <select
                      id="reg-referral-input"
                      value={form.referral_source}
                      onChange={(e) => {
                        const source = e.target.value;
                        update("referral_source", source);
                        if (source !== "ambassador") {
                          setAmbassadorInfo(null);
                          setReferralAccepted(false);
                          setForm((current) => ({ ...current, referral_source: source, referral_code: "", referral_clicked_at: "", referral_token: "" }));
                        }
                      }}
                      className={inputCls(false)}
                      data-testid="reg-referral-source"
                    >
                      <option value="">— اختر —</option>
                      <option value="twitter">تويتر / X</option>
                      <option value="instagram">إنستغرام</option>
                      <option value="tiktok">تيك توك</option>
                      <option value="linkedin">لينكدإن</option>
                      <option value="snapchat">سناب شات</option>
                      <option value="google">بحث جوجل</option>
                      <option value="friend">صديق</option>
                      <option value="mosque">جامع / مسجد</option>
                      <option value="ambassador">سفير عماد (رابط أو رمز إحالة)</option>
                      <option value="other">أخرى</option>
                    </select>
                    {form.referral_source === "ambassador" && !ambassadorInfo && (
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                        <input
                          value={form.referral_code}
                          onChange={(e) => {
                            update("referral_code", e.target.value.toUpperCase());
                            setAmbassadorInfo(null);
                            setReferralAccepted(false);
                          }}
                          placeholder="أدخل رمز سفير عماد"
                          dir="ltr"
                          className={inputCls(false)}
                          data-testid="reg-ambassador-code"
                          aria-label="رمز سفير عماد"
                        />
                        <button
                          type="button"
                          onClick={() => validateReferralCode()}
                          disabled={validatingReferral || !form.referral_code.trim()}
                          className="min-h-12 shrink-0 rounded-xl bg-teal-700 px-5 font-bold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
                          data-testid="validate-ambassador-code"
                        >
                          {validatingReferral ? "جارٍ التحقق…" : "تحقق من الرمز"}
                        </button>
                      </div>
                    )}
                    {form.referral_source === "ambassador" && !ambassadorInfo && form.referral_code && (
                      <p className="mt-1.5 text-xs text-slate-600">تحقق من الرمز لإظهار اسم السفير وتأكيد الإحالة.</p>
                    )}
                    {form.referral_source === "other" && (
                      <input
                        value={form.referral_other}
                        onChange={(e) => update("referral_other", e.target.value)}
                        placeholder="اذكر كيف عرفت عن عماد"
                        className={`${inputCls(false)} mt-2`}
                        data-testid="reg-referral-other"
                        aria-label="مصدر أخرى"
                        maxLength={200}
                      />
                    )}
                  </div>

                  {error && (
                    <div className="p-3.5 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200/70 flex items-start gap-2" data-testid="reg-error" role="alert">
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl border-2 border-slate-200 bg-white hover:border-teal-400 transition-colors">
                    <input
                      type="checkbox"
                      required
                      checked={agreedTerms}
                      onChange={(e) => { setAgreedTerms(e.target.checked); markTouched("terms"); }}
                      onBlur={() => markTouched("terms")}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-500/40 shrink-0"
                      data-testid="reg-terms-checkbox"
                      aria-required="true"
                    />
                    <span className="text-sm text-slate-700 leading-relaxed">
                      أوافق على{" "}
                      <Link to="/terms" target="_blank" className="text-teal-700 font-semibold hover:underline" data-testid="reg-terms-link">الشروط والأحكام</Link>
                      {" "}و{" "}
                      <Link to="/privacy" target="_blank" className="text-teal-700 font-semibold hover:underline" data-testid="reg-privacy-link">سياسة الخصوصية</Link>
                      <span className="text-red-500"> *</span>
                    </span>
                  </label>
                  {touched.terms && !agreedTerms && (
                    <div className="text-xs text-red-600 flex items-center gap-1" data-testid="reg-terms-error">
                      <AlertCircle className="h-3.5 w-3.5" /> يجب الموافقة على الشروط والأحكام وسياسة الخصوصية للمتابعة
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !agreedTerms}
                    className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-l from-teal-800 to-teal-600 text-white font-bold text-sm px-4 py-3 shadow-lg shadow-teal-900/20 hover:shadow-xl hover:shadow-teal-900/30 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none transition-all"
                    data-testid="reg-submit-btn"
                  >
                    <span className="relative inline-flex items-center justify-center gap-2 w-full">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                      إنشاء الحساب
                    </span>
                  </button>

                  <div className="text-center text-sm text-slate-600 pt-1">
                    لديك حساب؟{" "}
                    <Link to="/login" className="text-teal-700 font-semibold hover:underline" data-testid="go-login-link">
                      تسجيل الدخول
                    </Link>
                  </div>
                </form>
              </div>

              <div className="px-8 sm:px-10 py-4 bg-slate-50/70 border-t border-slate-200/70 flex items-center gap-2 text-[11px] text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-teal-700 shrink-0" />
                نحمي بياناتك بأعلى معايير التشفير ولا نشاركها مع أي جهة.
              </div>
            </div>
          </div>
        </main>

        {/* Right — brand storytelling text (over the shared full-screen background) */}
        <aside className="hidden lg:flex relative flex-col justify-between p-14 xl:p-16 text-white order-1 lg:order-2">
          <div className="relative z-10">
            <Link to="/" className="inline-block">
              <Logo variant="transparent" size={120} textColor="text-white" subColor="text-white/80" />
            </Link>
          </div>

          <div className="relative z-10">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-3 drop-shadow">
              انضم إلينا
            </div>
            <h2 className="text-4xl font-heading font-extrabold leading-tight max-w-md drop-shadow-lg">
              ابدأ رحلتك في عماد اليوم
            </h2>
            <p className="mt-4 text-teal-50 max-w-md drop-shadow">
              اختر الدور الذي يناسبك وابدأ رحلتك الرقمية معنا في بيئة تدريب تعاوني متكاملة.
            </p>
          </div>

          <div className="relative z-10 text-[11px] text-white/70 drop-shadow">
            © عماد {new Date().getFullYear()} — كل الحقوق محفوظة
          </div>
        </aside>
      </div>
    </div>
  );
}

function ReqRow({ ok, text, testid }) {
  const Icon = ok ? CheckCircle2 : Circle;
  return (
    <li
      className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-slate-500"}`}
      data-testid={testid}
      data-satisfied={ok ? "true" : "false"}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{text}</span>
    </li>
  );
}

function TrustChip({ title, caption }) {
  return (
    <div className="rounded-xl bg-white/[0.06] backdrop-blur-md border border-white/10 px-4 py-3">
      <div className="text-sm font-bold text-white">{title}</div>
      <div className="text-[11px] text-teal-100/70 mt-0.5">{caption}</div>
    </div>
  );
}
